import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Layout from '../../components/Layout';
import PermissionGuard from '../../components/PermissionGuard';
import { Permissions } from '../../constants/Permissions';
import { Product, Warehouse } from '../../types';
import { stockService, AdjustStockRequest } from '../../services/stockService';
import { productService } from '../../services/productService';
import { parseApiError } from '../../utils/errorUtils';

const StockAdjustmentPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [products, setProducts] = useState<Product[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentQuantity, setCurrentQuantity] = useState<number | null>(null);

  const [formData, setFormData] = useState<AdjustStockRequest>({
    productId: location.state?.productId || 0,
    warehouseId: location.state?.warehouseId || 0,
    newQuantity: 0,
    reason: '',
    notes: '',
  });

  const adjustmentReasons = [
    'Physical Count Discrepancy',
    'Damage/Loss',
    'Return from Customer',
    'Stock Transfer',
    'Inventory Correction',
    'Supplier Return',
    'Other',
  ];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const [productsRes, warehousesRes] = await Promise.all([
        productService.getProducts({ pageSize: 100 }),
        // For now, warehouses will be fetched from a warehouse service when available
        // For now, using mock data
      ]);

      if (productsRes.data.data) {
        setProducts(productsRes.data.data);
      }

      // Mock warehouse data - replace with real API call when warehouse service is available
      setWarehouses([
        { id: 1, code: 'WH001', name: 'Main Warehouse', location: 'Downtown', isActive: true },
        { id: 2, code: 'WH002', name: 'Secondary Warehouse', location: 'Suburbs', isActive: true },
      ]);

      setLoading(false);
    } catch (err: unknown) {
      setError(parseApiError(err, 'Failed to load form data').message);
      setLoading(false);
    }
  };

  const handleProductChange = (productId: number) => {
    setFormData((prev) => ({
      ...prev,
      productId,
    }));
    setCurrentQuantity(null);
  };

  const handleWarehouseChange = (warehouseId: number) => {
    setFormData((prev) => ({
      ...prev,
      warehouseId,
    }));
    setCurrentQuantity(null);
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'newQuantity' ? parseInt(value) : value,
    }));
  };

  const validateForm = (): boolean => {
    if (!formData.productId) {
      setError('Please select a product');
      return false;
    }
    if (!formData.warehouseId) {
      setError('Please select a warehouse');
      return false;
    }
    if (formData.newQuantity < 0) {
      setError('New quantity cannot be negative');
      return false;
    }
    if (!formData.reason.trim()) {
      setError('Please select a reason for adjustment');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) {
      return;
    }

    setSaving(true);
    try {
      await stockService.adjustStock(formData);
      navigate('/stock/levels', {
        state: { message: 'Stock adjusted successfully!' },
      });
    } catch (err: unknown) {
      setError(parseApiError(err, 'Failed to adjust stock').message);
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="container-fluid text-center mt-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </Layout>
    );
  }

  const selectedProduct = products.find((p) => p.id === formData.productId);

  return (
    <Layout>
      <div className="container-fluid">
        <div className="d-flex align-items-center mb-4">
          <button
            className="btn btn-link p-0 me-3"
            onClick={() => navigate('/stock/levels')}
          >
            ← Back to Stock Levels
          </button>
          <h1>Adjust Stock</h1>
        </div>

        {error && (
          <div className="alert alert-danger alert-dismissible fade show">
            {error}
          </div>
        )}

        <div className="row">
          <div className="col-lg-8">
            <div className="card">
              <div className="card-body">
                <form onSubmit={handleSubmit}>
                  <div className="mb-3">
                    <label className="form-label">
                      Product <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select"
                      value={formData.productId}
                      onChange={(e) => handleProductChange(parseInt(e.target.value))}
                      required
                    >
                      <option value={0}>-- Select a Product --</option>
                      {products.map((product) => (
                        <option key={product.id} value={product.id}>
                          {product.sku} - {product.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">
                      Warehouse <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select"
                      value={formData.warehouseId}
                      onChange={(e) => handleWarehouseChange(parseInt(e.target.value))}
                      required
                    >
                      <option value={0}>-- Select a Warehouse --</option>
                      {warehouses.map((warehouse) => (
                        <option key={warehouse.id} value={warehouse.id}>
                          {warehouse.name} ({warehouse.code})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Current Quantity</label>
                        <input
                          type="number"
                          className="form-control"
                          value={currentQuantity ?? 'Loading...'}
                          disabled
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">
                          New Quantity <span className="text-danger">*</span>
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          name="newQuantity"
                          value={formData.newQuantity}
                          onChange={handleInputChange}
                          min="0"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">
                      Reason <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select"
                      name="reason"
                      value={formData.reason}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">-- Select Reason --</option>
                      {adjustmentReasons.map((reason) => (
                        <option key={reason} value={reason}>
                          {reason}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Notes</label>
                    <textarea
                      className="form-control"
                      name="notes"
                      value={formData.notes || ''}
                      onChange={handleInputChange}
                      rows={4}
                      placeholder="Additional details about this adjustment..."
                    />
                  </div>

                  <div className="d-flex gap-2">
                    <PermissionGuard claim={Permissions.Stock.Adjust}>
                      <button
                        type="submit"
                        className="btn-save"
                        disabled={saving}
                      >
                        {saving ? 'Saving...' : 'Adjust Stock'}
                      </button>
                    </PermissionGuard>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => navigate('/stock/levels')}
                      disabled={saving}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>

          <div className="col-lg-4">
            <div className="card">
              <div className="card-header">
                <h5 className="mb-0">Selected Product Info</h5>
              </div>
              <div className="card-body small">
                {selectedProduct ? (
                  <>
                    <div className="mb-3">
                      <strong>SKU</strong>
                      <div>
                        <code>{selectedProduct.sku}</code>
                      </div>
                    </div>
                    <div className="mb-3">
                      <strong>Product Name</strong>
                      <div>{selectedProduct.name}</div>
                    </div>
                    <div className="mb-3">
                      <strong>Unit of Measure</strong>
                      <div>{selectedProduct.unitOfMeasure}</div>
                    </div>
                    <div className="mb-3">
                      <strong>Reorder Level</strong>
                      <div>{selectedProduct.reorderLevel}</div>
                    </div>
                    <div className="mb-3">
                      <strong>Reorder Quantity</strong>
                      <div>{selectedProduct.reorderQuantity}</div>
                    </div>
                  </>
                ) : (
                  <p className="text-muted">Select a product to view details</p>
                )}
              </div>
            </div>

            <div className="card mt-3">
              <div className="card-header">
                <h5 className="mb-0">Adjustment Information</h5>
              </div>
              <div className="card-body small">
                <div className="mb-3">
                  <strong>Difference</strong>
                  <div>
                    {currentQuantity !== null
                      ? formData.newQuantity - currentQuantity
                      : 'N/A'}
                  </div>
                </div>
                <p className="text-muted mb-0">
                  Enter the new quantity and select a reason for the adjustment. This will create an audit record.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default StockAdjustmentPage;
